import { useCallback, useEffect, useRef, useState } from 'react';
import Seo from '../../components/Seo';
import { PageLoader, ErrorState, Pagination, Field, Spinner, EmptyState } from '../../components/ui';
import { facebookApi } from '../../api/facebook';
import { useAsync } from '../../lib/useAsync';
import { useToast } from '../../context/ToastContext';
import { formatDateTime, relativeTime, cx } from '../../lib/format';

const STATUS_PILL = {
  posted: 'bg-green-100 text-green-800',
  imported: 'bg-blue-100 text-blue-800',
  pending: 'bg-gold-100 text-gold-800',
  failed: 'bg-red-100 text-red-700',
  cancelled: 'bg-surface-gray text-ink-soft',
};

const CONTENT_PATH = { news: 'news', activity: 'activities', event: 'events', story: 'stories', event_recap: 'events' };

/** ISO -> value for <input type="datetime-local"> (local time, minutes precision). */
function isoToLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function Toggle({ label, hint, checked, onChange, disabled }) {
  return (
    <label className="flex items-start gap-3 text-sm text-ink-soft">
      <input
        type="checkbox"
        className="mt-1 h-4 w-4 accent-maroon-700"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
      />
      <span>
        <span className="block font-medium text-ink">{label}</span>
        {hint && <span className="block text-xs text-ink-muted">{hint}</span>}
      </span>
    </label>
  );
}

export default function AdminFacebook() {
  const toast = useToast();
  const status = useAsync(() => facebookApi.status(), []);

  const [automation, setAutomation] = useState({ auto_publish: true, auto_import: true, import_since: '' });
  const [dirty, setDirty] = useState(false);
  const dirtyRef = useRef(false); // mirrors `dirty` for the seeding effect without being a dependency
  const [savingAutomation, setSavingAutomation] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const [page, setPage] = useState(1);
  const [log, setLog] = useState({ loading: true, error: null, items: [], meta: null });
  const [retrying, setRetrying] = useState(null);

  function updateAutomation(patch) {
    setAutomation((a) => ({ ...a, ...patch }));
    dirtyRef.current = true;
    setDirty(true);
  }

  useEffect(() => {
    if (!status.data || dirtyRef.current) return; // never overwrite unsaved edits
    setAutomation({
      auto_publish: !!status.data.auto_publish,
      auto_import: !!status.data.auto_import,
      import_since: isoToLocalInput(status.data.import_since),
    });
  }, [status.data]);

  const loadLog = useCallback(() => {
    setLog((s) => ({ ...s, loading: true, error: null }));
    facebookApi
      .posts({ page, per_page: 20 })
      .then((res) => setLog({ loading: false, error: null, items: res.items, meta: res.meta }))
      .catch((error) => setLog({ loading: false, error, items: [], meta: null }));
  }, [page]);

  useEffect(() => {
    loadLog();
  }, [loadLog]);

  async function saveAutomation(e) {
    e.preventDefault();
    setSavingAutomation(true);
    try {
      await facebookApi.saveSettings({
        auto_publish: automation.auto_publish,
        auto_import: automation.auto_import,
        import_since: automation.import_since ? new Date(automation.import_since).toISOString() : null,
      });
      toast.success('Automation settings saved.');
      dirtyRef.current = false;
      setDirty(false);
    } catch (err) {
      toast.error(err?.message || 'Could not save settings.');
    } finally {
      setSavingAutomation(false);
    }
  }

  async function syncNow() {
    setSyncing(true);
    try {
      const r = await facebookApi.sync();
      if (r.reason) {
        toast.error(`Sync did not run: ${r.reason.replace(/_/g, ' ')}${r.error ? ` — ${r.error}` : ''}.`);
      } else {
        toast.success(`Sync done — imported ${r.imported}, skipped ${r.skipped}, failed ${r.failed}, retried ${r.retried}.`);
      }
      status.reload();
      loadLog();
    } catch (err) {
      toast.error(err?.message || 'Sync failed.');
    } finally {
      setSyncing(false);
    }
  }

  async function retry(row) {
    setRetrying(row.id);
    try {
      const updated = await facebookApi.retry(row.id);
      if (updated?.status === 'posted') {
        toast.success('Posted to Facebook.');
      } else if (updated?.status === 'cancelled') {
        toast.info(`Retry cancelled: ${updated?.last_error || 'the content is no longer published.'}`);
      } else {
        toast.error(`Still failing: ${updated?.last_error || 'unknown error'}`);
      }
      loadLog();
    } catch (err) {
      toast.error(err?.message || 'Retry failed.');
    } finally {
      setRetrying(null);
    }
  }

  if (status.loading && !status.data) return <PageLoader label="Loading Facebook status…" />;
  if (status.error) return <ErrorState error={status.error} onRetry={status.reload} />;

  const s = status.data || {};
  const connected = s.enabled && s.configured && s.page && !s.page_error;

  return (
    <div className="space-y-8">
      <Seo title="Facebook Sync" />
      <div>
        <h1 className="font-display text-2xl font-bold">Facebook Sync</h1>
        <p className="text-ink-muted">
          Published News, Activities and Events are posted to the Facebook Page; new Page posts are imported as News.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Connection */}
        <section className="card p-6" aria-labelledby="fb-connection">
          <h2 id="fb-connection" className="mb-4 text-lg font-semibold text-ink">Connection</h2>
          {connected ? (
            <div className="flex items-center gap-3">
              <span className="pill bg-green-100 text-green-800">Connected</span>
              <span className="text-ink">
                {s.page.name} <span className="text-xs text-ink-muted">(Page ID {s.page.id})</span>
              </span>
            </div>
          ) : (
            <div className="space-y-2">
              <span className="pill bg-red-100 text-red-700">Not connected</span>
              {!s.enabled && <p className="text-sm text-ink-soft">Sync is switched off on the server (FACEBOOK_SYNC_ENABLED).</p>}
              {s.enabled && !s.configured && (
                <p className="text-sm text-ink-soft">
                  Page ID and access token are not configured. Follow the &ldquo;Facebook Page sync&rdquo; section of the backend README.
                </p>
              )}
              {s.page_error && <p className="text-sm text-red-700">{s.page_error}</p>}
            </div>
          )}
        </section>

        {/* Automation */}
        <section className="card p-6" aria-labelledby="fb-automation">
          <h2 id="fb-automation" className="mb-4 text-lg font-semibold text-ink">Automation</h2>
          <form onSubmit={saveAutomation} className="space-y-4">
            <Toggle
              label="Auto-post to Facebook"
              hint="Publish a Page post whenever News, an Activity or an Event is published."
              checked={automation.auto_publish}
              onChange={(v) => updateAutomation({ auto_publish: v })}
            />
            <Toggle
              label="Auto-import from Facebook"
              hint="Import new Page posts as published News every 5 minutes."
              checked={automation.auto_import}
              onChange={(v) => updateAutomation({ auto_import: v })}
            />
            <Field
              label="Import posts created after"
              hint="To backfill, move this date earlier and press Sync now — a manual sync pages back through up to 250 posts created after it. The automatic 5-minute sync only checks the 25 newest."
            >
              <input
                type="datetime-local"
                className="input"
                value={automation.import_since}
                onChange={(e) => updateAutomation({ import_since: e.target.value })}
              />
            </Field>
            <div className="flex items-center gap-3">
              <button type="submit" className="btn-primary" disabled={savingAutomation}>
                {savingAutomation ? <Spinner className="h-4 w-4" /> : 'Save settings'}
              </button>
              {dirty && <span className="text-xs text-ink-muted">Unsaved changes</span>}
            </div>
          </form>
        </section>
      </div>

      {/* Sync */}
      <section className="card p-6" aria-labelledby="fb-sync">
        <h2 id="fb-sync" className="mb-4 text-lg font-semibold text-ink">Sync</h2>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="space-y-1 text-sm text-ink-soft">
            <p>
              Last synced:{' '}
              <span className="font-medium text-ink">
                {s.last_synced_at ? `${relativeTime(s.last_synced_at)} (${formatDateTime(s.last_synced_at)})` : 'never'}
              </span>
            </p>
            <p className="flex items-center gap-2">
              Scheduler:
              {s.scheduler_healthy ? (
                <span className="pill bg-green-100 text-green-800">healthy</span>
              ) : (
                <>
                  <span className="pill bg-gold-100 text-gold-800">no recent run</span>
                  <span className="text-xs text-ink-muted">check the cron job on the server</span>
                </>
              )}
            </p>
            {s.last_sync_result && (
              <p className="text-xs text-ink-muted">
                Last run: fetched {s.last_sync_result.fetched ?? 0}, imported {s.last_sync_result.imported ?? 0}, skipped{' '}
                {s.last_sync_result.skipped ?? 0}, failed {s.last_sync_result.failed ?? 0}
              </p>
            )}
          </div>
          <button type="button" className="btn-primary" onClick={syncNow} disabled={syncing || !s.configured || !s.enabled}>
            {syncing ? <Spinner className="h-4 w-4" /> : 'Sync now'}
          </button>
        </div>
      </section>

      {/* Recent activity */}
      <section className="card overflow-hidden" aria-labelledby="fb-log">
        <div className="flex items-center justify-between px-6 py-4">
          <h2 id="fb-log" className="text-lg font-semibold text-ink">Recent activity</h2>
          <button type="button" className="btn-ghost px-3 py-1.5 text-xs" onClick={loadLog}>Refresh</button>
        </div>
        {log.loading ? (
          <PageLoader label="Loading sync log…" />
        ) : log.error ? (
          <div className="p-6"><ErrorState error={log.error} onRetry={loadLog} /></div>
        ) : log.items.length === 0 ? (
          <EmptyState title="Nothing synced yet" message="Rows appear here once content is posted to or imported from Facebook." icon="📘" />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="border-b border-cream-300 bg-surface-alt text-xs uppercase tracking-wider text-ink-soft">
                <tr>
                  <th className="px-5 py-3 text-left">Content</th>
                  <th className="px-5 py-3 text-left">Direction</th>
                  <th className="px-5 py-3 text-left">Status</th>
                  <th className="px-5 py-3 text-left">Facebook</th>
                  <th className="px-5 py-3 text-left">Attempts</th>
                  <th className="px-5 py-3 text-left">Error</th>
                  <th className="px-5 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-cream-300">
                {log.items.map((row) => (
                  <tr key={row.id} className="hover:bg-surface-alt">
                    <td className="px-5 py-3 font-medium text-ink">
                      {row.content ? (
                        <a
                          href={`/${CONTENT_PATH[row.content.type] || 'news'}/${row.content.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:text-maroon-700"
                        >
                          {row.content.title}
                        </a>
                      ) : (
                        <span className="text-ink-muted">(deleted content)</span>
                      )}
                      {row.content && <span className="ml-2 text-xs uppercase text-ink-muted">{row.content.type}</span>}
                    </td>
                    <td className="px-5 py-3 text-ink-soft">{row.direction === 'inbound' ? 'From Facebook' : 'To Facebook'}</td>
                    <td className="px-5 py-3">
                      <span className={cx('pill', STATUS_PILL[row.status] || 'bg-surface-gray text-ink-soft')}>{row.status}</span>
                    </td>
                    <td className="px-5 py-3">
                      {row.permalink_url ? (
                        <a href={row.permalink_url} target="_blank" rel="noopener noreferrer" className="text-maroon-700 underline">
                          Open post
                        </a>
                      ) : (
                        <span className="text-ink-muted">—</span>
                      )}
                      {row.posted_at && <div className="text-xs text-ink-muted">{formatDateTime(row.posted_at)}</div>}
                    </td>
                    <td className="px-5 py-3 text-ink-muted">{row.attempts}</td>
                    <td className="max-w-xs truncate px-5 py-3 text-xs text-red-700" title={row.last_error || ''}>
                      {row.last_error || ''}
                    </td>
                    <td className="px-5 py-3 text-right">
                      {row.direction === 'outbound' && row.status === 'failed' && (
                        <button
                          type="button"
                          className="btn-outline px-3 py-1.5 text-xs"
                          onClick={() => retry(row)}
                          disabled={retrying === row.id}
                        >
                          {retrying === row.id ? 'Retrying…' : 'Retry'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="px-6 py-4">
          <Pagination meta={log.meta} onChange={setPage} />
        </div>
      </section>
    </div>
  );
}
